import { useState, useEffect, useCallback } from "react";
import { isEmpty } from "lodash-es";

const getInitialParams = (initialParams, syncWithUrl) => {
  if (!syncWithUrl) return { ...initialParams };

  const searchParams = new URLSearchParams(window.location.search);
  const paramsFromUrl = {};

  Object.keys(initialParams).forEach((key) => {
    const valueFromUrl = searchParams.get(key);
    if (valueFromUrl !== null) {
      try {
        paramsFromUrl[key] = JSON.parse(decodeURIComponent(valueFromUrl)); // 解析 JSON 格式
      } catch (error) {
        paramsFromUrl[key] = valueFromUrl; // 若解析失敗，則保持原值
      }
    }
  });

  return { ...initialParams, ...paramsFromUrl };
};

const useTableParams = (initialParams = {}, options = {}) => {
  const { syncWithUrl = true, debounceTime = 300 } = options;
  const [params, setParamsInternal] = useState(() =>
    getInitialParams(initialParams, syncWithUrl)
  );
  const [debouncedParams, setDebouncedParams] = useState(params);

  useEffect(() => {
    if (!syncWithUrl) return;

    const handler = setTimeout(() => setDebouncedParams(params), debounceTime);
    return () => clearTimeout(handler);
  }, [params, debounceTime, syncWithUrl]);

  const updateUrl = useCallback(() => {
    if (!syncWithUrl) return;
    const newSearchParams = new URLSearchParams();

    Object.keys(initialParams).forEach((key) => {
      let value = debouncedParams[key];

      if (typeof value === "object" && value !== null) {
        value = JSON.stringify(value); // 轉換為 JSON 字串
      }

      if (
        value !== undefined &&
        value !== null &&
        value !== JSON.stringify(initialParams[key])
      ) {
        newSearchParams.set(key, encodeURIComponent(value));
      }
    });

    window.history.replaceState(
      {},
      "",
      newSearchParams.toString()
        ? `${window.location.pathname}?${newSearchParams}`
        : window.location.pathname
    );
  }, [debouncedParams, initialParams, syncWithUrl]);

  useEffect(updateUrl, [debouncedParams, updateUrl]);

  return {
    params,
    setParam: (key, value) =>
      setParamsInternal((prev) => ({ ...prev, [key]: value })),
    setParams: (newParams) =>
      setParamsInternal((prev) => ({ ...prev, ...newParams })),
    resetParams: () => setParamsInternal({ ...initialParams }),
  };
};

export default useTableParams;
